import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import {
  Recycle, UploadCloud, Loader2, CheckCircle2, AlertCircle,
  Trash2, RefreshCw, ChevronDown, BarChart3, List,
  Package, MapPin, User as UserIcon, FileText, FileSpreadsheet,
  AlertTriangle, Wand2, Download, Edit3, X, Save, Mail,
} from "lucide-react";
import { toast } from "sonner";
import SendEmailModal from "../components/SendEmailModal";

export default function PuntoLimpio() {
  const { authAxios, user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [contractId, setContractId] = useState("");
  const [tickets, setTickets] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [monthlyLer, setMonthlyLer] = useState(null);
  const [lerMonth, setLerMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [view, setView] = useState("list"); // list | dashboard
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [onlyReview, setOnlyReview] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [expanded, setExpanded] = useState({}); // ticketId -> boolean
  const [raeeCatalog, setRaeeCatalog] = useState({ raee: [], general: [] });
  const [editing, setEditing] = useState(null); // {ticket, index, entry}
  const [savingEdit, setSavingEdit] = useState(false);
  const [revStats, setRevStats] = useState(null);
  const [showRevisions, setShowRevisions] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [emailTarget, setEmailTarget] = useState(null); // { kind: "list"|"monthly-xlsx"|"monthly-pdf" }
  const fileInputRef = useRef(null);

  const isAdmin = user?.role === "admin" || user?.role === "manager";

  const selectedContract = contracts.find(c => c.id === contractId);
  const isGuadalajara = !!selectedContract && (
    selectedContract.code === "5700341" ||
    /guadalajara/i.test(selectedContract.name || "")
  );

  useEffect(() => {
    (async () => {
      try {
        const { data } = await authAxios.get("/contracts");
        setContracts(data);
        if (data.length && !contractId) setContractId(data[0].id);
      } catch (e) { console.error(e); }
      try {
        const { data: cat } = await authAxios.get("/punto-limpio/raee-catalog");
        setRaeeCatalog(cat);
      } catch (e) { console.error("raee catalog", e); }
    })();
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (contractId) params.contract_id = contractId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [{ data: list }, { data: dash }, { data: lerM }, { data: stats }] = await Promise.all([
        authAxios.get("/punto-limpio", { params }),
        authAxios.get("/punto-limpio/dashboard", { params }),
        authAxios.get("/punto-limpio/monthly-ler-summary", { params: { ...params, month: lerMonth } }),
        authAxios.get("/punto-limpio/revisions/stats", { params }),
      ]);
      setTickets(list);
      setDashboard(dash);
      setMonthlyLer(lerM);
      setRevStats(stats);
    } catch (e) {
      console.error(e);
      toast.error("Error cargando tickets de Punto Limpio");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (contractId) refresh(); }, [contractId, lerMonth, dateFrom, dateTo]);

  const openRevisions = async () => {
    try {
      const params = {};
      if (contractId) params.contract_id = contractId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await authAxios.get("/punto-limpio/revisions", { params });
      setRevisions(data);
      setShowRevisions(true);
    } catch (e) {
      console.error(e);
      toast.error("No se pudo cargar el historial");
    }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    if (!contractId) { toast.error("Selecciona un contrato primero"); return; }
    setUploading(true);
    const form = new FormData();
    form.append("contract_id", contractId);
    Array.from(files).forEach(f => form.append("files", f));
    try {
      const { data } = await authAxios.post("/punto-limpio/upload", form, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`${data.total_files} documento(s) en proceso. Refresca en unos segundos.`);
      // Poll batch
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const { data: batch } = await authAxios.get(`/punto-limpio/batch/${data.batch_id}`);
          if (batch.status !== "processing" || attempts > 30) {
            clearInterval(poll);
            refresh();
            if (batch.status === "completed") {
              const pr = batch.entries_pending_review || 0;
              if (pr > 0) {
                toast.warning(`Procesados ${batch.processed} · ${batch.entries_created} líneas · ${pr} necesita${pr === 1 ? "" : "n"} revisión de LER`, { duration: 8000 });
              } else {
                toast.success(`Procesados ${batch.processed} · ${batch.entries_created} líneas extraídas`);
              }
            }
            else if (batch.status === "partial") {
              const pr = batch.entries_pending_review || 0;
              toast.warning(`${batch.processed} ok, ${batch.errors} con error${pr ? ` · ${pr} línea(s) a revisar` : ""}`);
            }
            else if (batch.status === "failed") toast.error("Todos los documentos fallaron");
          }
        } catch (err) { clearInterval(poll); }
      }, 3000);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Error subiendo documentos");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (ticketId) => {
    if (!window.confirm("¿Eliminar este ticket de punto limpio?")) return;
    try {
      await authAxios.delete(`/punto-limpio/${ticketId}`);
      toast.success("Ticket eliminado");
      refresh();
    } catch (e) { toast.error("No se pudo eliminar"); }
  };

  const handleMigrate = async () => {
    if (!window.confirm("Aplicar el mapeo LER a los tickets ya creados. ¿Continuar?")) return;
    setMigrating(true);
    try {
      const { data } = await authAxios.post("/admin/punto-limpio/migrate-ler");
      toast.success(`Migración OK · ${data.tickets_updated}/${data.tickets_scanned} tickets actualizados · ${data.entries_enriched} líneas enriquecidas · ${data.entries_flagged} pendientes de revisión`);
      refresh();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Error migrando tickets");
    } finally { setMigrating(false); }
  };

  const downloadListXlsx = async (fmt = "standard") => {
    try {
      const params = {};
      if (contractId) params.contract_id = contractId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (onlyReview) params.only_review = true;
      if (fmt && fmt !== "standard") params.format = fmt;
      const { data } = await authAxios.get("/punto-limpio/export.xlsx", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().slice(0,16).replace(/[-:T]/g, "");
      const prefix = fmt === "guadalajara" ? "punto_limpio_guadalajara" : "punto_limpio_listado";
      link.download = `${prefix}_${stamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(fmt === "guadalajara" ? "Excel Guadalajara descargado" : "Listado exportado a Excel");
    } catch (e) {
      console.error(e);
      toast.error("No se pudo descargar el listado");
    }
  };

  const downloadReport = async (fmt) => {
    try {
      const params = { month: lerMonth };
      if (contractId) params.contract_id = contractId;
      const path = fmt === "pdf" ? "/punto-limpio/monthly-ler-summary/export.pdf"
                                 : "/punto-limpio/monthly-ler-summary/export.xlsx";
      const { data } = await authAxios.get(path, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `informe_ler_punto_limpio_${lerMonth}.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error("No se pudo descargar el informe");
    }
  };

  const filteredTickets = onlyReview
    ? tickets.filter(t => (t.entries || []).some(e => e.needs_review))
    : tickets;

  const openEdit = (ticket, index) => {
    const entry = (ticket.entries || [])[index] || {};
    setEditing({
      ticket,
      index,
      form: {
        waste_type: entry.waste_type || "",
        waste_category: entry.waste_category || "",
        ler_code: entry.ler_code || "",
        ler_grupo: entry.ler_grupo || "",
        quantity: entry.quantity ?? "",
        unit: entry.unit || "uds",
        observations: entry.observations || "",
      },
    });
  };

  const applyRaeePreset = (item) => {
    if (!editing) return;
    setEditing(prev => ({
      ...prev,
      form: { ...prev.form, ler_code: item.ler, ler_grupo: item.grupo, waste_type: prev.form.waste_type || item.item },
    }));
  };

  const saveEntryEdit = async () => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      const t = editing.ticket;
      const newEntries = (t.entries || []).map((e, i) => {
        if (i !== editing.index) return e;
        const f = editing.form;
        // Al asignar un ler_code manual, se limpia el needs_review.
        const updated = { ...e,
          waste_type: f.waste_type, waste_category: f.waste_category,
          ler_code: f.ler_code, ler_grupo: f.ler_grupo,
          quantity: f.quantity === "" ? null : Number(f.quantity),
          unit: f.unit, observations: f.observations,
        };
        if (f.ler_code) { delete updated.needs_review; delete updated.review_reason; }
        return updated;
      });
      await authAxios.patch(`/punto-limpio/${t.id}`, { entries: newEntries });
      toast.success("Línea actualizada");
      setEditing(null);
      refresh();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "No se pudo guardar");
    } finally { setSavingEdit(false); }
  };

  return (
    <div className="space-y-6" data-testid="punto-limpio-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 flex items-center gap-3">
            <Recycle size={28} className="text-[#007934]" />
            Punto Limpio Móvil
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Sube los partes manuscritos del punto limpio móvil y deja que la IA extraiga la lista de residuos.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <input
            type="date"
            data-testid="pl-date-from"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-2 py-1.5 border border-slate-300 rounded-lg text-xs bg-white"
            title="Desde"
          />
          <span className="text-slate-400 text-xs">→</span>
          <input
            type="date"
            data-testid="pl-date-to"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-2 py-1.5 border border-slate-300 rounded-lg text-xs bg-white"
            title="Hasta"
          />
          {(dateFrom || dateTo) && (
            <button
              data-testid="pl-date-clear"
              onClick={() => { setDateFrom(""); setDateTo(""); }}
              className="text-xs text-slate-500 hover:text-slate-700 underline"
              title="Limpiar rango"
            >
              limpiar
            </button>
          )}
          <div className="relative">
            <select
              data-testid="pl-contract-select"
              value={contractId}
              onChange={(e) => setContractId(e.target.value)}
              className="appearance-none pl-3 pr-10 py-2 border border-slate-300 rounded-lg text-sm font-medium bg-white text-slate-700 min-w-[220px]"
            >
              <option value="">Todos mis contratos</option>
              {contracts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button data-testid="pl-view-list" onClick={() => setView("list")}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-semibold ${view === "list" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
              <List size={14} /> Lista
            </button>
            <button data-testid="pl-view-dashboard" onClick={() => setView("dashboard")}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-semibold ${view === "dashboard" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
              <BarChart3 size={14} /> Dashboard
            </button>
          </div>
          <button data-testid="pl-refresh" onClick={refresh} disabled={loading}
            className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
          <button
            data-testid="pl-export-list-xlsx"
            onClick={() => downloadListXlsx("standard")}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700"
            title="Descargar listado completo de tickets y líneas a Excel"
          >
            <FileSpreadsheet size={13} /> Exportar Excel
          </button>
          <button
            data-testid="pl-email-list-xlsx"
            onClick={() => setEmailTarget({ kind: "list" })}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50"
            title="Enviar listado completo por email"
          >
            <Mail size={13} /> Enviar por email
          </button>
          {isGuadalajara && (
            <button
              data-testid="pl-export-list-xlsx-guadalajara"
              onClick={() => downloadListXlsx("guadalajara")}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold bg-amber-600 text-white hover:bg-amber-700"
              title="Formato solicitado por el Ayuntamiento de Guadalajara (Fecha · Ubicación · CP · Cantidad · Tipo · Operario)"
            >
              <FileSpreadsheet size={13} /> Formato Guadalajara
            </button>
          )}
        </div>
      </div>

      {/* Upload zone */}
      <div className="bg-white border-2 border-dashed border-slate-300 rounded-2xl p-6 text-center">
        <UploadCloud size={30} className="mx-auto text-slate-400 mb-2" />
        <p className="text-sm text-slate-600 mb-3">
          Arrastra o selecciona los partes manuscritos del Punto Limpio Móvil (PDF, JPG, PNG). Se procesan varios a la vez.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleUpload}
          className="hidden"
          data-testid="pl-file-input"
        />
        <button
          data-testid="pl-upload-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || !contractId}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#007934] hover:bg-[#005c27] text-white font-semibold rounded-lg text-sm disabled:opacity-50"
        >
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />}
          {uploading ? "Subiendo..." : "Seleccionar documentos"}
        </button>
        {!contractId && <p className="text-xs text-amber-600 mt-2">Selecciona un contrato primero</p>}
      </div>

      {/* Banner alertas RAEE + Acciones admin */}
      {(dashboard?.pending_review > 0 || isAdmin) && (
        <div className="flex flex-wrap items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3"
             data-testid="pl-review-banner">
          <div className="flex items-center gap-3">
            {dashboard?.pending_review > 0 ? (
              <>
                <AlertTriangle size={20} className="text-amber-600 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-semibold text-amber-900" data-testid="pl-review-count">
                    {dashboard.pending_review} línea{dashboard.pending_review === 1 ? "" : "s"} necesita{dashboard.pending_review === 1 ? "" : "n"} revisión de LER
                  </p>
                  <p className="text-amber-700 text-xs">
                    Materiales no reconocidos o RAEE genérico. Revisa y asigna el código LER específico.
                  </p>
                </div>
              </>
            ) : (
              <>
                <CheckCircle2 size={20} className="text-emerald-600 flex-shrink-0" />
                <p className="text-sm text-slate-600">Sin líneas pendientes de revisión de LER en el periodo actual.</p>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {dashboard?.pending_review > 0 && (
              <button
                data-testid="pl-toggle-review-filter"
                onClick={() => setOnlyReview(v => !v)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border ${onlyReview ? "bg-amber-600 text-white border-amber-600" : "bg-white text-amber-700 border-amber-300 hover:bg-amber-50"}`}
              >
                <AlertTriangle size={13} />
                {onlyReview ? "Ver todos" : "Sólo pendientes"}
              </button>
            )}
            {isAdmin && (
              <button
                data-testid="pl-migrate-btn"
                onClick={handleMigrate}
                disabled={migrating}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50"
              >
                {migrating ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />}
                {migrating ? "Migrando…" : "Aplicar mapeo LER a tickets antiguos"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Dashboard */}
      {view === "dashboard" && dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard icon={Package} label="Tickets procesados" value={dashboard.total_tickets} />
          <StatCard icon={List} label="Líneas de residuo" value={dashboard.total_entries} color="#B45309" />
          <StatCard icon={MapPin} label="Ubicaciones" value={dashboard.by_location?.length || 0} color="#2563EB" />
          <StatCard icon={UserIcon} label="Operarios" value={dashboard.top_operators?.length || 0} color="#7C3AED" />

          <div className="md:col-span-2 card-white p-5">
            <h3 className="text-sm font-bold text-slate-700 mb-3">Por categoría de residuo</h3>
            {(!dashboard.by_category || dashboard.by_category.length === 0) ? (
              <p className="text-xs text-slate-400">Sin datos</p>
            ) : (
              <div className="space-y-2">
                {dashboard.by_category.map(c => (
                  <div key={c.category} className="flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-700">{c.category}</span>
                    <span className="font-bold text-[#007934]">{c.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="md:col-span-2 card-white p-5">
            <h3 className="text-sm font-bold text-slate-700 mb-3">Top ubicaciones</h3>
            {(!dashboard.by_location || dashboard.by_location.length === 0) ? (
              <p className="text-xs text-slate-400">Sin datos</p>
            ) : (
              <div className="space-y-2">
                {dashboard.by_location.map(l => (
                  <div key={l.location} className="flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-700 truncate max-w-[70%]">{l.location}</span>
                    <span className="font-bold text-slate-800">{l.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="md:col-span-4 card-white p-5" data-testid="pl-by-ler-card">
            <h3 className="text-sm font-bold text-slate-700 mb-3">Por código LER (RAEE específico)</h3>
            {(!dashboard.by_ler || dashboard.by_ler.length === 0) ? (
              <p className="text-xs text-slate-400">Sin datos con LER asignado en el periodo</p>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-500 uppercase">
                    <th className="text-left py-1">LER</th>
                    <th className="text-left py-1">Grupo</th>
                    <th className="text-right py-1">Líneas</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.by_ler.map((l, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="py-1 font-mono text-blue-700 font-semibold">{l.code}</td>
                      <td className="py-1 text-slate-600">{l.grupo || "—"}</td>
                      <td className="py-1 text-right font-bold">{l.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Calidad OCR / historial de revisiones */}
          {revStats && (
            <div className="md:col-span-4 card-white p-5" data-testid="pl-ocr-quality-card">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  Calidad OCR & Historial de revisiones LER
                </h3>
                <button
                  data-testid="pl-view-revisions"
                  onClick={openRevisions}
                  className="text-xs font-semibold text-[#007934] hover:underline"
                >
                  Ver historial detallado →
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
                <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
                  <p className="text-emerald-700">Calidad OCR</p>
                  <p className="font-bold text-emerald-900 text-2xl leading-tight" data-testid="pl-ocr-quality-pct">
                    {revStats.ocr_quality_pct}%
                  </p>
                  <p className="text-[10px] text-emerald-700">líneas sin revisión manual</p>
                </div>
                <div className="bg-slate-50 rounded-lg px-3 py-2">
                  <p className="text-slate-500">Revisiones manuales</p>
                  <p className="font-bold text-slate-800 text-2xl leading-tight">{revStats.total_revisions}</p>
                  <p className="text-[10px] text-slate-500">sobre {revStats.total_entries} líneas</p>
                </div>
                <div className="bg-slate-50 rounded-lg px-3 py-2">
                  <p className="text-slate-500">Desde pendientes</p>
                  <p className="font-bold text-slate-800 text-2xl leading-tight">{revStats.from_pending_review}</p>
                  <p className="text-[10px] text-slate-500">rescatadas por operarios</p>
                </div>
                <div className="bg-slate-50 rounded-lg px-3 py-2">
                  <p className="text-slate-500">% Revisadas</p>
                  <p className="font-bold text-slate-800 text-2xl leading-tight">{revStats.pct_revised}%</p>
                  <p className="text-[10px] text-slate-500">del total de líneas</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div>
                  <p className="font-bold text-slate-600 uppercase text-[10px] mb-1">Top revisores</p>
                  {(revStats.by_reviewer || []).length === 0 ? (
                    <p className="text-slate-400 text-xs">Sin revisiones aún</p>
                  ) : (
                    <div className="space-y-1">
                      {revStats.by_reviewer.map((r, i) => (
                        <div key={i} className="flex justify-between border-b border-slate-100 py-1">
                          <span className="text-slate-700">{r.name}</span>
                          <span className="font-bold text-[#007934]">{r.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <p className="font-bold text-slate-600 uppercase text-[10px] mb-1">Operarios con líneas revisadas</p>
                  {(revStats.by_operator || []).length === 0 ? (
                    <p className="text-slate-400 text-xs">Sin datos</p>
                  ) : (
                    <div className="space-y-1">
                      {revStats.by_operator.map((r, i) => (
                        <div key={i} className="flex justify-between border-b border-slate-100 py-1">
                          <span className="text-slate-700">{r.operator}</span>
                          <span className="font-bold text-amber-700">{r.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Informe mensual por LER — visible siempre para reporting rápido */}
      <div className="card-white p-5" data-testid="pl-monthly-ler-card">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
            <FileText size={16} className="text-[#007934]" />
            Informe mensual por LER (Punto Limpio)
          </h3>
          <div className="flex items-center gap-2">
            <input
              type="month"
              value={lerMonth}
              onChange={(e) => setLerMonth(e.target.value)}
              data-testid="pl-monthly-ler-month"
              className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
            />
            <button
              data-testid="pl-export-xlsx"
              onClick={() => downloadReport("xlsx")}
              disabled={!monthlyLer || (monthlyLer.items || []).length === 0}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              <FileSpreadsheet size={13} /> Excel
            </button>
            <button
              data-testid="pl-export-pdf"
              onClick={() => downloadReport("pdf")}
              disabled={!monthlyLer || (monthlyLer.items || []).length === 0}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
            >
              <Download size={13} /> PDF
            </button>
            <button
              data-testid="pl-email-monthly"
              onClick={() => setEmailTarget({ kind: "monthly" })}
              disabled={!monthlyLer || (monthlyLer.items || []).length === 0}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              title="Enviar informe mensual LER por email"
            >
              <Mail size={13} /> Email
            </button>
          </div>
        </div>
        {!monthlyLer || (monthlyLer.items || []).length === 0 ? (
          <p className="text-xs text-slate-400">Sin líneas de Punto Limpio para el mes seleccionado.</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-3 text-xs">
              <div className="bg-slate-50 rounded-lg px-3 py-2">
                <p className="text-slate-500">Tickets</p>
                <p className="font-bold text-slate-800 text-base">{monthlyLer.total_tickets}</p>
              </div>
              <div className="bg-slate-50 rounded-lg px-3 py-2">
                <p className="text-slate-500">Líneas</p>
                <p className="font-bold text-slate-800 text-base">{monthlyLer.total_lines}</p>
              </div>
              <div className="bg-slate-50 rounded-lg px-3 py-2">
                <p className="text-slate-500">Unidades totales</p>
                <p className="font-bold text-slate-800 text-base">{monthlyLer.total_units}</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase">
                  <tr>
                    <th className="text-left px-2 py-1">LER</th>
                    <th className="text-left px-2 py-1">Grupo</th>
                    <th className="text-left px-2 py-1">Descripción</th>
                    <th className="text-right px-2 py-1">Líneas</th>
                    <th className="text-right px-2 py-1">Unidades</th>
                    <th className="text-right px-2 py-1">% sobre líneas</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyLer.items.map((it, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-2 py-1 font-mono font-semibold text-blue-700">{it.code}</td>
                      <td className="px-2 py-1 text-slate-600">{it.grupo || "—"}</td>
                      <td className="px-2 py-1 text-slate-500">{it.description || "—"}</td>
                      <td className="px-2 py-1 text-right font-bold">{it.lines}</td>
                      <td className="px-2 py-1 text-right">{it.units}</td>
                      <td className="px-2 py-1 text-right">{it.pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* List */}
      {view === "list" && (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {filteredTickets.length === 0 ? (
            <div className="p-10 text-center">
              <Recycle size={32} className="mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">
                {onlyReview
                  ? "No hay tickets con líneas pendientes de revisión."
                  : "Aún no hay tickets procesados. Sube los primeros documentos arriba."}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Fecha</th>
                  <th className="text-left px-4 py-3">Ubicación</th>
                  <th className="text-left px-4 py-3">Operario</th>
                  <th className="text-center px-4 py-3">Líneas</th>
                  <th className="text-left px-4 py-3">Documento</th>
                  <th className="text-right px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredTickets.map(t => {
                  const reviewCount = (t.entries || []).filter(e => e.needs_review).length;
                  return (
                  <React.Fragment key={t.id}>
                    <tr className={`border-t border-slate-100 hover:bg-slate-50 ${reviewCount > 0 ? "bg-amber-50/50" : ""}`}>
                      <td className="px-4 py-3 font-medium text-slate-700">{t.date || "—"}</td>
                      <td className="px-4 py-3 text-slate-700">{t.location || "—"}</td>
                      <td className="px-4 py-3 text-slate-700">{t.operator || "—"}</td>
                      <td className="px-4 py-3 text-center font-bold text-[#007934]">
                        {t.total_entries ?? (t.entries || []).length}
                        {reviewCount > 0 && (
                          <span
                            data-testid={`pl-ticket-review-badge-${t.id}`}
                            className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold"
                            title={`${reviewCount} línea(s) sin LER específico`}
                          >
                            <AlertTriangle size={10} /> {reviewCount}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500 truncate max-w-[220px]" title={t.document_name}>{t.document_name}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          data-testid={`pl-toggle-${t.id}`}
                          onClick={() => setExpanded(prev => ({ ...prev, [t.id]: !prev[t.id] }))}
                          className="text-xs text-[#007934] font-semibold hover:underline mr-3"
                        >
                          {expanded[t.id] ? "Cerrar" : "Ver líneas"}
                        </button>
                        <button
                          data-testid={`pl-delete-${t.id}`}
                          onClick={() => handleDelete(t.id)}
                          className="p-1.5 rounded hover:bg-red-50 text-red-500"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                    {expanded[t.id] && (t.entries || []).length > 0 && (
                      <tr>
                        <td colSpan={6} className="bg-slate-50 px-4 py-3">
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs" data-testid={`pl-entries-table-${t.id}`}>
                              <thead>
                                <tr className="text-slate-500 uppercase">
                                  <th className="text-left py-1">Código</th>
                                  <th className="text-right py-1">Cantidad</th>
                                  <th className="text-left py-1">Unidad</th>
                                  <th className="text-left py-1">Residuo (tal cual)</th>
                                  <th className="text-left py-1">Categoría</th>
                                  <th className="text-left py-1">LER</th>
                                  <th className="text-left py-1">Grupo RAEE</th>
                                  <th className="text-left py-1">Observaciones</th>
                                  <th className="text-right py-1"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {(t.entries || []).map((e, i) => (
                                  <tr key={i} className={`border-t border-slate-200 ${e.needs_review ? "bg-amber-50" : ""}`}>
                                    <td className="py-1 font-mono">{e.code || "—"}</td>
                                    <td className="py-1 text-right font-semibold">{e.quantity ?? "—"}</td>
                                    <td className="py-1">{(e.unit || "uds")}</td>
                                    <td className="py-1">
                                      {e.needs_review && (
                                        <AlertTriangle
                                          size={12}
                                          className="inline text-amber-600 mr-1 align-text-bottom"
                                          data-testid={`pl-entry-review-icon-${t.id}-${i}`}
                                        />
                                      )}
                                      {e.waste_type || "—"}
                                      {e.needs_review && e.review_reason && (
                                        <span className="block text-[10px] text-amber-700 italic">
                                          ⚠ {e.review_reason}
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-1">
                                      {e.waste_category && (
                                        <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold">
                                          {e.waste_category}
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-1 font-mono text-slate-700" data-testid={`pl-entry-ler-${t.id}-${i}`}>
                                      {e.ler_code ? (
                                        <span className={`inline-block px-2 py-0.5 rounded font-semibold ${e.needs_review ? "bg-amber-100 text-amber-800" : "bg-blue-50 text-blue-700"}`}>
                                          {e.ler_code}
                                        </span>
                                      ) : "—"}
                                    </td>
                                    <td className="py-1 text-slate-600 text-[11px]">{e.ler_grupo || "—"}</td>
                                    <td className="py-1 text-slate-500">{e.observations || ""}</td>
                                    <td className="py-1 text-right">
                                      <button
                                        data-testid={`pl-edit-entry-${t.id}-${i}`}
                                        onClick={() => openEdit(t, i)}
                                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${e.needs_review ? "bg-amber-600 text-white hover:bg-amber-700" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                                        title="Editar / asignar LER"
                                      >
                                        <Edit3 size={11} />
                                        {e.needs_review ? "Revisar" : "Editar"}
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Modal Edición Rápida LER */}
      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
          data-testid="pl-edit-modal"
          onClick={() => !savingEdit && setEditing(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <Edit3 size={18} className="text-[#007934]" />
                Edición rápida de línea
              </h3>
              <button
                data-testid="pl-edit-close"
                onClick={() => setEditing(null)}
                className="p-1 rounded hover:bg-slate-100 text-slate-500"
              >
                <X size={18} />
              </button>
            </div>

            {editing.form && (
              <div className="p-5 space-y-4 text-sm">
                {/* Presets RAEE rápidos */}
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">
                    Sugerencias RAEE (asigna LER específico con un clic)
                  </label>
                  <select
                    data-testid="pl-edit-raee-preset"
                    onChange={(e) => {
                      const item = raeeCatalog.raee.find(r => r.item === e.target.value);
                      if (item) applyRaeePreset(item);
                    }}
                    value=""
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white"
                  >
                    <option value="">— Selecciona un aparato RAEE del catálogo —</option>
                    {raeeCatalog.raee?.map(r => (
                      <option key={r.item} value={r.item}>
                        {r.item} → {r.ler} · {r.grupo}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Residuo</label>
                    <input
                      data-testid="pl-edit-waste-type"
                      value={editing.form.waste_type}
                      onChange={(e) => setEditing({ ...editing, form: { ...editing.form, waste_type: e.target.value } })}
                      className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Categoría</label>
                    <input
                      data-testid="pl-edit-waste-category"
                      value={editing.form.waste_category}
                      onChange={(e) => setEditing({ ...editing, form: { ...editing.form, waste_category: e.target.value } })}
                      className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Código LER</label>
                    <input
                      data-testid="pl-edit-ler-code"
                      list="pl-ler-datalist"
                      value={editing.form.ler_code}
                      onChange={(e) => setEditing({ ...editing, form: { ...editing.form, ler_code: e.target.value } })}
                      placeholder="p.ej. 200136-42"
                      className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-mono"
                    />
                    <datalist id="pl-ler-datalist">
                      {raeeCatalog.raee?.map((r, idx) => (
                        <option key={`raee-${idx}`} value={r.ler}>{r.item} · {r.grupo}</option>
                      ))}
                      {raeeCatalog.general?.map((g, idx) => (
                        <option key={`gen-${idx}`} value={g.code}>{g.description}</option>
                      ))}
                    </datalist>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Grupo RAEE</label>
                    <input
                      data-testid="pl-edit-ler-grupo"
                      value={editing.form.ler_grupo}
                      onChange={(e) => setEditing({ ...editing, form: { ...editing.form, ler_grupo: e.target.value } })}
                      className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Cantidad</label>
                    <input
                      data-testid="pl-edit-quantity"
                      type="number"
                      step="any"
                      value={editing.form.quantity}
                      onChange={(e) => setEditing({ ...editing, form: { ...editing.form, quantity: e.target.value } })}
                      className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm text-right"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Unidad</label>
                    <input
                      data-testid="pl-edit-unit"
                      value={editing.form.unit}
                      onChange={(e) => setEditing({ ...editing, form: { ...editing.form, unit: e.target.value } })}
                      className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Observaciones</label>
                  <textarea
                    data-testid="pl-edit-observations"
                    value={editing.form.observations}
                    onChange={(e) => setEditing({ ...editing, form: { ...editing.form, observations: e.target.value } })}
                    rows={2}
                    className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm resize-none"
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => setEditing(null)}
                disabled={savingEdit}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-white border border-slate-300 text-slate-700 hover:bg-slate-100"
              >
                Cancelar
              </button>
              <button
                data-testid="pl-edit-save"
                onClick={saveEntryEdit}
                disabled={savingEdit}
                className="inline-flex items-center gap-1 px-4 py-1.5 rounded-lg text-sm font-semibold bg-[#007934] text-white hover:bg-[#005c27] disabled:opacity-50"
              >
                {savingEdit ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal Historial de Revisiones LER */}
      {showRevisions && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
          data-testid="pl-revisions-modal"
          onClick={() => setShowRevisions(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <FileText size={18} className="text-[#007934]" />
                Historial de revisiones LER ({revisions.length})
              </h3>
              <button
                onClick={() => setShowRevisions(false)}
                className="p-1 rounded hover:bg-slate-100 text-slate-500"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              {revisions.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">Aún no hay revisiones manuales de LER en este periodo/contrato.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="pl-revisions-table">
                    <thead className="bg-slate-50 text-slate-500 uppercase">
                      <tr>
                        <th className="text-left px-2 py-1">Fecha revisión</th>
                        <th className="text-left px-2 py-1">Revisor</th>
                        <th className="text-left px-2 py-1">Residuo</th>
                        <th className="text-left px-2 py-1">Ticket op.</th>
                        <th className="text-left px-2 py-1">LER anterior</th>
                        <th className="text-left px-2 py-1">LER nuevo</th>
                        <th className="text-left px-2 py-1">Grupo</th>
                        <th className="text-center px-2 py-1">Pend?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revisions.map(r => (
                        <tr key={r.id} className="border-t border-slate-100">
                          <td className="px-2 py-1 text-slate-600 whitespace-nowrap">
                            {r.created_at?.slice(0, 16).replace("T", " ")}
                          </td>
                          <td className="px-2 py-1 font-semibold text-slate-800">{r.reviewer_name || r.reviewer_email}</td>
                          <td className="px-2 py-1 text-slate-700">{r.waste_type}</td>
                          <td className="px-2 py-1 text-slate-500">{r.operator || "—"}</td>
                          <td className="px-2 py-1 font-mono text-slate-500">{r.old_ler_code || "—"}</td>
                          <td className="px-2 py-1 font-mono">
                            <span className="inline-block px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-semibold">
                              {r.new_ler_code}
                            </span>
                          </td>
                          <td className="px-2 py-1 text-slate-600">{r.new_ler_grupo || "—"}</td>
                          <td className="px-2 py-1 text-center">
                            {r.was_pending_review ? (
                              <AlertTriangle size={12} className="inline text-amber-600" />
                            ) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <SendEmailModal
        open={!!emailTarget}
        onClose={() => setEmailTarget(null)}
        title={emailTarget?.kind === "list" ? "Enviar listado Punto Limpio" : "Enviar informe mensual LER"}
        attachmentLabel={emailTarget?.kind === "list" ? "punto_limpio_listado.xlsx" : `informe_ler_punto_limpio_${lerMonth}.pdf`}
        onSend={async ({ to, message }) => {
          if (emailTarget?.kind === "list") {
            await authAxios.post("/punto-limpio/email-xlsx", {
              to, message, contract_id: contractId,
              date_from: dateFrom || undefined, date_to: dateTo || undefined,
              only_review: onlyReview || undefined,
            });
          } else {
            await authAxios.post("/punto-limpio/monthly-ler-summary/email", {
              to, message, contract_id: contractId, month: lerMonth, format: "pdf",
            });
          }
        }}
      />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color = "#007934" }) {
  return (
    <div className="card-white p-5 flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: color + "18" }}>
        <Icon size={20} style={{ color }} />
      </div>
      <div>
        <p className="text-xs text-slate-400 font-medium">{label}</p>
        <p className="text-2xl font-bold text-slate-800 leading-tight">{value ?? "—"}</p>
      </div>
    </div>
  );
}
